import { cartClient } from '../common/cartClient';
import type {
  AssociateOrderRequest,
  AssociateOrderResponse,
  EditBillingAddressRequest,
  EditBillingAddressResponse,
  EditShippingAddressPayload,
  EditShippingAddressResponse,
  GetCustomerOrderRequest,
  GetCustomerOrderResponse,
  GetOrderByTrackingRequest,
  GetOrderByTrackingResponse,
  GetOrderRequest,
  GetOrderResponse,
  GetOrdersRequest,
  GetOrdersResponse,
  GetUserOrdersRequest,
  GetUserOrdersResponse,
} from './utils/types';

/**
 * API client for Order-related operations
 */
class OrderAPIClient {
  /**
   * Get a single order by ID
   * @param params - Order request parameters
   * @returns Promise with order data
   */
  public async getOrder({ orderId, ...params }: GetOrderRequest): Promise<GetOrderResponse> {
    return await cartClient.get(`/orders/${orderId}`, { params }).then((res) => res.data);
  }

  /**
   * Get a customer-facing order by ID via the Facade customer endpoint.
   * - Logged-in: session cookie proves customer_id ownership
   * - Guest confirmation: order-scoped HttpOnly confirmation cookie proves access
   * - Guest delivery tracking: an explicitly supplied email preserves the legacy lookup contract
   * - Mismatch or not found: server returns 404 (existence hiding)
   */
  public async getCustomerOrder({ orderId, email, view }: GetCustomerOrderRequest): Promise<GetCustomerOrderResponse> {
    const path = `/woocommerce/customer/orders/${orderId}`;
    const params = { ...(email !== undefined && { email }), ...(view !== undefined && { view }) };
    const response = Object.keys(params).length
      ? cartClient.get<GetCustomerOrderResponse>(path, { params })
      : cartClient.get<GetCustomerOrderResponse>(path);
    return await response.then((result) => result.data);
  }

  /**
   * Get list of orders
   * @param params - Orders list request parameters
   * @returns Promise with orders list
   */
  public async getOrders(params?: GetOrdersRequest): Promise<GetOrdersResponse> {
    return await cartClient.get('/orders', { params }).then((res) => res.data);
  }

  /**
   * Get the current user's order list cards (cursor-paginated) via the Facade
   * customer endpoint. The user is resolved from the session cookie, so no userId
   * is sent; the server returns 401 when the caller is not authenticated.
   */
  public async getUserOrders({ cursor, limit }: GetUserOrdersRequest): Promise<GetUserOrdersResponse> {
    const params = {
      ...(cursor && { cursor }),
      ...(limit && { limit }),
    };
    return await cartClient.get('/woocommerce/orders', { params }).then((res) => res.data);
  }

  /**
   * Associate an order with the authenticated user
   * @param params - Order association request parameters
   * @returns Promise with association result
   */
  public async associateOrder({ orderId }: AssociateOrderRequest): Promise<AssociateOrderResponse> {
    return await cartClient.post('/orders/associate', { orderId }).then((res) => res.data);
  }

  /**
   * Edit shipping address for an order within 5 minutes of creation
   * Used for Express Checkout when required fields are missing
   * @param params - Shipping address update parameters
   * @returns Promise with edit result
   */
  public async editShippingAddress({
    orderId,
    ...shippingData
  }: EditShippingAddressPayload): Promise<EditShippingAddressResponse> {
    return await cartClient.patch(`/order/${orderId}/editShippingAddress`, shippingData).then((res) => res.data);
  }

  /**
   * Edit billing address for an order within 5 minutes of creation
   * Used for Express Checkout to update billing address with PayPal payer data
   * @param params - Billing address update parameters
   * @returns Promise with edit result
   */
  public async editBillingAddress({
    orderId,
    ...billingData
  }: EditBillingAddressRequest): Promise<EditBillingAddressResponse> {
    return await cartClient.patch(`/order/${orderId}/editBillingAddress`, billingData).then((res) => res.data);
  }

  public async getOrderByTracking({ trackingNo }: GetOrderByTrackingRequest): Promise<GetOrderByTrackingResponse> {
    return await cartClient.get(`/orders/tracking/${trackingNo}`).then((res) => res.data);
  }
}

export const orderAPIClient = new OrderAPIClient();
